import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  ShieldCheck,
  Award,
  ShoppingCart,
  Truck,
  Receipt,
  CreditCard,
  CheckCircle2,
  ArrowRight,
  Zap,
  ExternalLink,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import {
  unifiedLifecycleCoordinator,
  ConnectedProcurementChain,
  LifecycleStageKey,
} from '@/services/mock/UnifiedLifecycleCoordinator';
import { ROUTES } from '@/constants/routes';

interface UnifiedProcurementLifecycleBarProps {
  requestId: string;
  className?: string;
  showSimulateButton?: boolean;
  onChainUpdate?: (chain: ConnectedProcurementChain) => void;
}

export const UnifiedProcurementLifecycleBar: React.FC<UnifiedProcurementLifecycleBarProps> = ({
  requestId,
  className = '',
  showSimulateButton = true,
  onChainUpdate,
}) => {
  const navigate = useNavigate();
  const [chain, setChain] = useState<ConnectedProcurementChain | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);

  const loadChain = async () => {
    try {
      const data = await unifiedLifecycleCoordinator.getChainForRequisition(requestId);
      setChain(data);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    loadChain();
  }, [requestId]);

  const handleSimulateFull = async () => {
    try {
      setIsSimulating(true);
      const completedChain = await unifiedLifecycleCoordinator.simulateFullFlow(requestId);
      setChain(completedChain);
      if (onChainUpdate) onChainUpdate(completedChain);
    } catch (err: any) {
      alert(err.message || 'Simulation error');
    } finally {
      setIsSimulating(false);
    }
  };

  const handleAdvanceNext = async () => {
    if (!chain) return;
    try {
      setIsSimulating(true);
      if (chain.stagesCompleted === 1) {
        const next = await unifiedLifecycleCoordinator.onManagerApproved(chain.prId, 'Manager manual signoff');
        setChain(next);
        if (onChainUpdate) onChainUpdate(next);
      } else if (chain.stagesCompleted === 2 && chain.nfa) {
        const next = await unifiedLifecycleCoordinator.onNfaApproved(chain.nfa.id, 'Executive Committee manual clearance');
        setChain(next);
        if (onChainUpdate) onChainUpdate(next);
      } else if (chain.stagesCompleted === 3 && chain.po) {
        const next = await unifiedLifecycleCoordinator.onPoDelivered(chain.po.id);
        setChain(next);
        if (onChainUpdate) onChainUpdate(next);
      } else if (chain.stagesCompleted === 4 && chain.grn) {
        const next = await unifiedLifecycleCoordinator.onGrnInvoiceMatched(chain.grn.id);
        setChain(next);
        if (onChainUpdate) onChainUpdate(next);
      } else if (chain.stagesCompleted === 5 && chain.invoice) {
        const next = await unifiedLifecycleCoordinator.onPaymentDisbursed(chain.invoice.id);
        setChain(next);
        if (onChainUpdate) onChainUpdate(next);
      }
    } catch (err: any) {
      alert(err.message || 'Failed to advance stage');
    } finally {
      setIsSimulating(false);
    }
  };

  const STAGES: Array<{
    key: LifecycleStageKey;
    label: string;
    sublabel: string;
    docNumber?: string;
    docPath?: string;
    icon: React.ElementType;
    stepIndex: number;
  }> = [
    {
      key: 'REQUEST',
      label: '1. Requisition',
      sublabel: 'Employee Created',
      docNumber: chain?.requestNumber || requestId,
      docPath: ROUTES.REQUESTS.DETAILS(chain?.prId || requestId),
      icon: FileText,
      stepIndex: 1,
    },
    {
      key: 'MANAGER_APPROVAL',
      label: '2. Manager Signoff',
      sublabel: 'Dept Approval',
      docNumber: chain?.stagesCompleted && chain.stagesCompleted >= 2 ? 'Authorized' : 'Pending',
      docPath: ROUTES.APPROVALS.LIST,
      icon: ShieldCheck,
      stepIndex: 2,
    },
    {
      key: 'NFA_APPROVAL',
      label: '3. NFA & Sourcing',
      sublabel: 'Commercial Clearance',
      docNumber: chain?.nfa?.nfaNumber || (chain?.stagesCompleted && chain.stagesCompleted >= 3 ? 'NFA Issued' : undefined),
      docPath: chain?.nfa ? `/nfa/${chain.nfa.id}` : ROUTES.APPROVALS.LIST,
      icon: Award,
      stepIndex: 3,
    },
    {
      key: 'PO_ISSUED',
      label: '4. Purchase Order',
      sublabel: 'Supplier Contract',
      docNumber: chain?.po?.poNumber,
      docPath: chain?.po ? ROUTES.PURCHASE_ORDERS.DETAILS(chain.po.id) : ROUTES.PURCHASE_ORDERS.LIST,
      icon: ShoppingCart,
      stepIndex: 4,
    },
    {
      key: 'GRN_ACCEPTED',
      label: '5. Goods Receipt',
      sublabel: 'Dock Inspection',
      docNumber: chain?.grn?.grnNumber,
      docPath: chain?.grn ? `/procurement/grn/${chain.grn.id}` : ROUTES.GRN.LIST,
      icon: Truck,
      stepIndex: 5,
    },
    {
      key: 'INVOICE_MATCHED',
      label: '6. 3-Way Match',
      sublabel: 'Accounts Payable',
      docNumber: chain?.invoice?.invoiceNumber,
      docPath: chain?.invoice ? `/procurement/invoices/${chain.invoice.id}` : ROUTES.INVOICES.LIST,
      icon: Receipt,
      stepIndex: 6,
    },
    {
      key: 'PAYMENT_SETTLED',
      label: '7. Payment Settled',
      sublabel: 'Treasury NEFT / SAP',
      docNumber: chain?.payment?.paymentReference,
      docPath: chain?.payment ? `/procurement/payments/${chain.payment.id}` : ROUTES.PAYMENTS.LIST,
      icon: CreditCard,
      stepIndex: 7,
    },
  ];

  const currentCompleted = chain?.stagesCompleted || 1;
  const isFullySettled = currentCompleted >= 7;

  return (
    <div className={`p-4 bg-surface rounded-2xl border border-border/80 shadow-xs font-poppins space-y-3.5 ${className}`}>
      {/* Top Header Row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-xs font-extrabold text-foreground tracking-tight">
                Unified Procurement Lifecycle
              </h4>
              <Badge
                variant="outline"
                className={`text-[10px] font-extrabold ${
                  isFullySettled
                    ? 'border-emerald-500/30 text-emerald-600 dark:text-emerald-400 bg-emerald-500/10'
                    : 'border-primary/30 text-primary bg-primary/10'
                }`}
              >
                {isFullySettled ? 'Stage 7 of 7: Payment Settled' : `Stage ${currentCompleted} of 7: In Progress`}
              </Badge>
            </div>
            <p className="text-[11px] text-foreground-muted">
              Live automated progression connecting Employee → Manager → NFA → PO → GRN → Invoice → Payment.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        {showSimulateButton && (
          <div className="flex items-center gap-2 shrink-0">
            {!isFullySettled && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleAdvanceNext}
                disabled={isSimulating}
                className="h-8 px-3 text-xs font-bold rounded-xl border-primary/30 text-primary hover:bg-primary/10 gap-1.5 cursor-pointer transition-all shadow-2xs"
                title="Advance this requisition to the next sequential stage"
              >
                <ArrowRight className="h-3.5 w-3.5" />
                <span>Advance Next Stage</span>
              </Button>
            )}

            {!isFullySettled && (
              <Button
                size="sm"
                onClick={handleSimulateFull}
                disabled={isSimulating}
                className="h-8 px-3 text-xs font-bold rounded-xl bg-gradient-to-r from-primary to-indigo-600 hover:from-primary/90 hover:to-indigo-600/90 text-white gap-1.5 cursor-pointer shadow-xs transition-all active:scale-95"
                title="Fast-forward the entire flow from Requisition to Payment in 1-click!"
              >
                <Zap className="h-3.5 w-3.5 fill-current" />
                <span>{isSimulating ? 'Simulating...' : 'Simulate Full Flow'}</span>
              </Button>
            )}

            {isFullySettled && (
              <Badge className="h-8 px-3 bg-emerald-600/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 font-extrabold text-xs gap-1.5">
                <CheckCircle2 className="h-4 w-4" /> Full Lifecycle Settled
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Horizontal Stepper Grid */}
      <div className="relative overflow-x-auto pb-1 pt-1 custom-scrollbar">
        <div className="flex items-start min-w-[780px] justify-between relative">
          {STAGES.map((stage, idx) => {
            const isCompleted = currentCompleted >= stage.stepIndex;
            const isCurrent = currentCompleted === stage.stepIndex - 1 || (currentCompleted === stage.stepIndex && !isFullySettled);
            const Icon = stage.icon;

            return (
              <div key={stage.key} className="flex-1 flex flex-col items-center relative group px-1">
                {/* Connector Line to Next Step */}
                {idx < STAGES.length - 1 && (
                  <div
                    className={`absolute top-4 left-[50%] right-[-50%] h-0.5 z-0 transition-colors duration-300 ${
                      currentCompleted > stage.stepIndex
                        ? 'bg-emerald-500'
                        : isCurrent
                        ? 'bg-primary/50 border-t border-dashed border-primary'
                        : 'bg-border'
                    }`}
                  />
                )}

                {/* Step Circle */}
                <div
                  className={`relative z-10 h-8 w-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isCompleted
                      ? 'bg-emerald-600 text-white shadow-xs'
                      : isCurrent
                      ? 'bg-primary text-white ring-4 ring-primary/20 shadow-xs'
                      : 'bg-muted border border-border text-foreground-muted'
                  }`}
                >
                  {isCompleted && currentCompleted > stage.stepIndex ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <Icon className="h-3.5 w-3.5" />
                  )}
                </div>

                {/* Step Text Info */}
                <div className="mt-2 text-center flex flex-col items-center space-y-0.5">
                  <span
                    className={`text-xs font-bold whitespace-nowrap block ${
                      isCompleted || isCurrent ? 'text-foreground font-extrabold' : 'text-foreground-muted'
                    }`}
                  >
                    {stage.label}
                  </span>

                  <span className="text-[10px] text-foreground-secondary block whitespace-nowrap">
                    {stage.sublabel}
                  </span>

                  {/* Document Reference Chip Link */}
                  {stage.docNumber ? (
                    <button
                      type="button"
                      onClick={() => stage.docPath && navigate(stage.docPath)}
                      className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono font-bold transition-all cursor-pointer ${
                        isCompleted
                          ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20 hover:bg-emerald-500/20'
                          : 'bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20'
                      }`}
                      title={`Click to open ${stage.docNumber}`}
                    >
                      <span>{stage.docNumber}</span>
                      <ExternalLink className="h-2.5 w-2.5" />
                    </button>
                  ) : (
                    <span className="mt-1 text-[9px] font-mono text-foreground-muted block">
                      {isCompleted ? 'Authorized' : 'Awaiting'}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
